import React from 'react';
import styles from './Style.module.css';
import { useNavigate, useLocation as useRouterLocation } from 'react-router-dom';
import { useUI } from '../../Context/UI/UIContext';
import { useAppointment } from '../../Context/Appointment/AppointmentContext';
import { getBackPath } from '../../Utils/NavigationMap';
import { ArrowLeft } from 'lucide-react';
import { BtnClose } from '../Buttons/BtnClose/BtnClose';
import { useBookingSession } from '../../Context/BookingSession/BookingSessionContext';
import { useSchedule } from '../../Context/Schedule/ScheduleContext';
import { useGuests } from '../../Context/Guests/GuestsContext';
import { clearSessionStorage } from '../../Utils/SessionStorage';
import { useLocation } from '../../Context/Location/LocationContext';
import { getSessionStorageValue, STORAGE_KEYS } from '../../Utils/SessionStorage';
import useBookingTimer from '../../Context/Timer/useBookingTimer';
import { useVoucherContext } from '../../Context/Voucher/VoucherContext';

const VOUCHER_ROUTES = ["/select-vouchers", "/billing-details", "/review-voucher-purchase"];

export const Header = ({
  title,
  description,
  noCross = false,
  noServiceSelected = false,
  noVoucherSelected = false,
  onBack,
}) => {
  const { appointmentTypeID, setAppointmentTypeID } = useAppointment();
  const { setIsExitCardActive, setIsDiscardPopupActive } = useUI();
  const { bookingID, setBookingID, uuid, bookingUpdate } = useBookingSession();
  const { setSelectedDate, setSelectedTime } = useSchedule();
  const { resetMembers, setPrimaryCustomer } = useGuests();
  const { setSelectedOutletID } = useLocation();
  const { setIsLeaveBookingPopupActive } = useBookingTimer();
  const { clearVoucherCart } = useVoucherContext();

  const { pathname } = useRouterLocation();
  const navigate = useNavigate();
  const isVoucherRoute = VOUCHER_ROUTES.includes(pathname);
  const skipExitConfirm = isVoucherRoute ? noVoucherSelected : noServiceSelected;
  const appointmentUuid = bookingUpdate && uuid ? uuid : null;

  const Exit = () => {
    if (appointmentUuid) {
      setIsDiscardPopupActive(true);
    } else {
      setIsExitCardActive(true);
    }
  };

  const handleBack = () => {
    // Use custom onBack handler if provided
    if (onBack) {
      onBack();
      return;
    }

    if (pathname === '/outlets') {
      if (appointmentUuid) {
        navigate(`/appointment/${appointmentUuid}`);
      } else {
        setAppointmentTypeID(null);
        setIsExitCardActive(false);
        resetMembers();
        setPrimaryCustomer(null);
        setSelectedOutletID(null);
        setBookingID(null);
        setSelectedTime('');
        setSelectedDate('');
        clearSessionStorage({ preserveUuid: true });
        navigate('/');
      }
      return;
    }

    // Leaving Review → Time: release hold and stop the countdown (new bookings only).
    const holdId = bookingID || getSessionStorageValue(STORAGE_KEYS.BOOKING_ID);
    if (pathname === '/review-and-confirm-booking' && holdId && !appointmentUuid) {
      setIsLeaveBookingPopupActive(true);
      return;
    }

    // Payment → Contact Details: keep the existing hold and countdown.
    const target = getBackPath(pathname, appointmentTypeID);
    navigate(target);
  };

  const exitToHome = () => {
  setIsExitCardActive(false);

  if (isVoucherRoute) {
    clearVoucherCart();
    navigate('/');
    return;
  }

  if (appointmentUuid) {
    navigate(`/appointment/${appointmentUuid}`);
    return;
  }

  setAppointmentTypeID(null);
  resetMembers();
  setPrimaryCustomer(null);
  setSelectedOutletID(null);
  setBookingID(null);
  setSelectedTime('');
  setSelectedDate('');
  clearSessionStorage({ preserveUuid: true });

  navigate('/');
};

  return (
    <div className={`${styles.header} header space-x`}>
      <div className="container-md">
        <div className={styles.row}>
          <div className={styles.leftCol}>
            <div className={styles.leftRow}>
              <button 
              onClick={handleBack} 
              className={styles.left} 
              aria-label="Go Back"
              title="Go Back"
              > <ArrowLeft size={20} color="var(--Neutral900)" /></button>
              <h1 className="title-main after-scroll text-capitalize">{title}</h1>
            </div>
          </div>
          <div className={styles.rightCol}>
            {noCross ? "" : 
            <BtnClose 
            handleClick={skipExitConfirm ? exitToHome : Exit} 
            customClass={styles.right}
            title="Close"
            />
            }
          </div>
        </div>
        <div className="title-container">
          <div className="title-content">
            <h1 className="title-main before-scroll text-capitalize">{title}</h1>
            {description && <p className="title-sub">{description}</p>}
          </div>
        </div>
      </div>
    </div>
  )
}
